'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import Icon from '@/components/ui/AppIcon';

export default function HelpCenterPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: 'How do I place an order?',
      answer: 'Simply sign up for a free account, select your desired service, upload your images, and submit your order. You\'ll receive your edited images within 24 hours.'
    },
    {
      question: 'What is the turnaround time?',
      answer: 'Our standard turnaround time is 24 hours for most services. Rush delivery options are available for an additional fee if you need your images sooner.'
    },
    {
      question: 'What file formats do you accept?',
      answer: 'We accept all common image formats including JPG, PNG, TIFF, and RAW files. For best results, we recommend uploading high-resolution images.'
    },
    {
      question: 'How much does it cost?',
      answer: 'Pricing varies by service. Image enhancement starts at $1.60 per image, virtual staging at $24 per image, and day to dusk at $4 per image. Volume discounts are available.'
    },
    {
      question: 'Can I request revisions?',
      answer: 'Yes! We offer unlimited free revisions until you\'re completely satisfied with the results. Your satisfaction is our priority.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use industry-standard encryption and secure servers to protect your images and personal information. Your files are never shared with third parties.'
    },
    {
      question: 'Do you offer bulk discounts?',
      answer: 'Yes! Orders of 20+ images automatically receive discounted pricing. Contact us for custom enterprise pricing for larger volumes.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, PayPal, and bank transfers for enterprise accounts. Payment is processed securely through Stripe.'
    }
  ];

  const gettingStartedSteps = [
    {
      step: '1',
      title: 'Create Account',
      description: 'Sign up for free in less than 60 seconds',
      icon: '👤'
    },
    {
      step: '2',
      title: 'Upload Images',
      description: 'Select your service and upload your photos',
      icon: '📤'
    },
    {
      step: '3',
      title: 'Review & Approve',
      description: 'Receive edited images within 24 hours',
      icon: '✅'
    },
    {
      step: '4',
      title: 'Download',
      description: 'Download your professional images',
      icon: '⬇️'
    }
  ];

  const commonIssues = [
    {
      issue: 'Upload Failed',
      solution: 'Ensure your file size is under 50MB and in a supported format (JPG, PNG, TIFF, RAW). Try using a different browser if the issue persists.',
      icon: '⚠️'
    },
    {
      issue: 'Payment Declined',
      solution: 'Verify your card details are correct and you have sufficient funds. Contact your bank if the issue continues, or try a different payment method.',
      icon: '💳'
    },
    {
      issue: 'Can\'t Download Images',
      solution: 'Check your internet connection and ensure pop-ups are enabled. Try right-clicking the download button and selecting "Save Link As".',
      icon: '⬇️'
    },
    {
      issue: 'Image Quality Issues',
      solution: 'Request a free revision through your order dashboard. Provide specific feedback on what you\'d like adjusted, and we\'ll make it right.',
      icon: '🖼️'
    }
  ];

  return (
    <div className="bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="min-h-[500px] bg-gradient-to-br from-slate-900 via-slate-800 to-green-900 flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00em0wIDI0YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00ek0xMiAxNmMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHptMCAyNGMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        <div className="w-full px-4 lg:px-8 py-20 lg:py-28 relative z-10">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Help Center
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Find answers to common questions, learn how to get started, and get the most out of 
              Shashio's professional photo editing services.
            </p>
          </div>
        </div>
      </section>

      {/* Getting Started Section */}
      <section className="py-20 lg:py-32 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 mb-6">
              Getting Started
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              New to Shashio? Follow these simple steps to get your first project started.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {gettingStartedSteps.map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-6xl mb-6">{item.icon}</div>
                <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link href="/auth/signup">
              <button className="px-10 py-4 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 transition-all duration-200 shadow-lg">
                Create Free Account
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-32 px-4 lg:px-8 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Quick answers to questions you may have about our services.
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:border-green-300 transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-bold text-slate-900 pr-4">{faq.question}</span>
                  <Icon 
                    name={openFaq === index ? "ChevronUpIcon" : "ChevronDownIcon"} 
                    size={24} 
                    variant="outline"
                    className="flex-shrink-0 text-green-600"
                  />
                </button>
                {openFaq === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Issues Section */}
      <section className="py-20 lg:py-32 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 mb-6">
              Common Issues & Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Troubleshoot common problems and find quick solutions to get back on track.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {commonIssues.map((item, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-green-300 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="text-5xl flex-shrink-0">{item.icon}</div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{item.issue}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Support Section */}
      <section className="py-20 lg:py-32 px-4 lg:px-8 bg-gradient-to-r from-green-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Still Need Help?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Can't find what you're looking for? Our support team is here to help. 
            Reach out and we'll get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/contact">
              <button className="px-10 py-4 bg-white text-green-600 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all duration-200 shadow-lg">
                Contact Support
              </button>
            </Link>
            <a href="mailto:support@shashio.com">
              <button className="px-10 py-4 bg-transparent border-2 border-white text-white rounded-lg font-bold text-lg hover:bg-white hover:text-green-600 transition-all duration-200">
                Email Us
              </button>
            </a>
          </div>
          <p className="text-sm text-green-100 mt-6">
            Email: support@shashio.com | Response time: Within 24 hours
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
